import React, { useCallback, useEffect, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { toast } from "react-toastify"
import { getAuditLogs } from "../../requesters/Backend/AuditLogRequester"
import { convertSnakeToTitle } from "../../util"
import Pagination from "../Pagination"

const PER_PAGE = 20

// Renders a human-readable summary of an audit log's metadata.
const formatMetadata = metadata => {
  if (!metadata) return "—"
  if (metadata.changes) {
    return Object.entries(metadata.changes)
      .map(([field, [from, to]]) => `${convertSnakeToTitle(field)}: ${from ?? "—"} → ${to ?? "—"}`)
      .join(", ")
  }
  return Object.entries(metadata)
    .map(([key, value]) => `${convertSnakeToTitle(key)}: ${value ?? "—"}`)
    .join(", ")
}

function BackendActivityLogsDashboard({ baseUrl, currentUser }) {
  const location = useLocation()
  const navigate = useNavigate()
  const initialParams = new URLSearchParams(location.search)
  const programId = initialParams.get("program_id") || undefined

  const [logs, setLogs] = useState([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(parseInt(initialParams.get("page") || "1", 10))
  const [query, setQuery] = useState(initialParams.get("search") || "")
  const [debouncedQuery, setDebouncedQuery] = useState(initialParams.get("search") || "")

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(t)
  }, [query])

  const fetchLogs = useCallback(() => {
    setIsLoading(true)
    const params = { page: currentPage, per_page: PER_PAGE }
    if (programId) params.program_id = programId
    if (debouncedQuery) params.search = debouncedQuery
    getAuditLogs(baseUrl, params, currentUser)
      .then(res => {
        setLogs(res?.response || [])
        setTotal(parseInt(res?.headers?.["x-total"] || "0", 10))
        setIsLoading(false)
        return null
      })
      .catch(() => {
        setIsLoading(false)
        toast.error("Could not load activity logs")
      })
  }, [baseUrl, currentUser, currentPage, debouncedQuery, programId])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  useEffect(() => {
    const params = new URLSearchParams()
    params.set("page", String(currentPage))
    if (programId) params.set("program_id", programId)
    if (debouncedQuery) params.set("search", debouncedQuery)
    const next = `?${params.toString()}`
    if (location.search === next) return
    navigate({ pathname: location.pathname, search: next }, { replace: true })
  }, [currentPage, debouncedQuery, programId, navigate, location.pathname, location.search])

  const handleSearch = event => {
    setQuery(event.target.value)
    setCurrentPage(1)
  }

  return (
    <div className="content">
      <div className="container-fluid">
        <div className="page-header">
          <div className="d-flex justify-content-between align-items-center">
            <h2>Activity Logs</h2>
          </div>
        </div>
        <div className="table-filters d-inline-flex">
          <input
            type="search"
            className="form-control"
            placeholder="e.g. update, john@example.com"
            value={query}
            onChange={handleSearch} />
        </div>
        { isLoading ? (
          <div className="d-flex justify-content-center align-items-center">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
          ) : (
            <div className="list-table-wrap">
              <div className="table-responsive">
                <table className="table list-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Action</th>
                      <th>By</th>
                      <th>Type</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length !== 0 ? (
                      logs.map(log => (
                        <tr key={log.id || `${log.action}-${log.created_at}-${log.auditable_id}`}>
                          <td className="text-nowrap cell-muted">{log.created_at}</td>
                          <td className="text-nowrap">{log.action}</td>
                          <td>{log.actor_email}</td>
                          <td>{log.auditable_type ? <span className="cell-badge">{log.auditable_type}</span> : "—"}</td>
                          <td className="cell-muted">{formatMetadata(log.metadata)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">
                          {debouncedQuery ? "No activity matches your search" : "No activity yet"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        <Pagination
          totalRows={total}
          pageChangeHandler={setCurrentPage}
          rowsPerPage={PER_PAGE}
          initialPage={currentPage} />
      </div>
    </div>
  )
}

export default BackendActivityLogsDashboard
